import { useEffect, useCallback, useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  type SortingState
} from "@tanstack/react-table";
import { TooltipProvider } from "@radix-ui/react-tooltip";

import { Table } from "@/components/ui/table";

import { cn } from "@/lib/utils";
import {
  useGetData,
  useInvalidateQueries
} from "@/hooks/services/use-query-hooks";

import {
  DataTableHeader,
  DataTableBody,
  DataTablePagination,
  ActionCell
} from "./components";

import { useTableState } from "./hooks";
import {
  DEFAULT_COLUMN_CONFIG,
  calculateMinColumnWidth,
  ACTION_COLUMN_WIDTH,
  SELECT_COLUMN_WIDTH
} from "./utils";
import type {
  DataTableProps,
  QueryResult,
  ColumnDef,
  EditingCell
} from "./types";

const DEFAULT_FEATURES = {
  enableSelection: true,
  enableRowDragAndDrop: false,
  enableColumnDragAndDrop: true,
  enablePinning: true,
  enableResizing: true,
  enableColumnReorder: true,
  enableEditing: false,
  enableExpanding: false,
  enableSorting: true,
  enablePagination: true
};

const DEFAULT_PAGE_SIZES = [10, 20, 50, 100];

export function DataTable<TData, TResponse extends QueryResult<TData>>({
  api,
  columns,
  filterParams,
  actionItems,
  actionColumnCell,
  headerActions,
  onRefetch,
  onInvalidate,
  onTableReady,
  onSelectionChange,
  onEditValue,
  onRowOrderChange,
  onRowDoubleClick,
  onActionClick: _onActionClick,
  onAddSubRow,
  onForeignKeySearch,
  subRowKey,
  identifierKey,
  features = {},
  persistence,
  pageSizes = DEFAULT_PAGE_SIZES,
  defaultPageSize = 10,
  isLoading: externalLoading,
  emptyState,
  className
}: DataTableProps<TData, TResponse>) {
  const tableFeatures = { ...DEFAULT_FEATURES, ...features };

  const tableState = useTableState({
    persistence,
    defaultPageSize,
    onSelectionChange
  });

  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const invalidateQueries = useInvalidateQueries();

  const apiParams = useMemo(
    () => ({
      ...filterParams,
      ...tableState.pagination,
      ...(tableState.sorting.length
        ? {
            sortBy:
              (columns.find((c) => c.id === tableState.sorting[0].id) as any)
                ?.attribute ?? tableState.sorting[0].id,
            orderBy: tableState.sorting[0].desc ? "desc" : "asc"
          }
        : null)
    }),
    [filterParams, tableState.pagination, tableState.sorting, columns]
  );

  const { data, refetch, isLoading, isRefetching } = useGetData<TResponse>(
    api(apiParams)
  );
  useEffect(() => {
    if (onRefetch) {
      onRefetch(refetch);
    }
  }, [refetch, onRefetch]);

  const queryKey = useMemo(() => {
    const apiResult = api(apiParams);

    return apiResult.name.includes("_")
      ? apiResult.name.split("_")
      : [apiResult.name];
  }, [api, apiParams]);

  useEffect(() => {
    if (onInvalidate) {
      onInvalidate(() => {
        invalidateQueries(queryKey);
      });
    }
  }, [onInvalidate, invalidateQueries, queryKey]);

  const builtColumns = useMemo(() => {
    const cols: ColumnDef<TData>[] = [];

    if (tableFeatures.enableSelection) {
      cols.push({
        id: "select",
        header: () => null,
        cell: () => null,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        size: SELECT_COLUMN_WIDTH,
        minSize: SELECT_COLUMN_WIDTH,
        maxSize: SELECT_COLUMN_WIDTH
      });
    }

    const mappedColumns = columns.map((col) => {
      if (!tableFeatures.enableResizing || col.minSize !== undefined) {
        return col;
      }

      const accessorKey = "accessorKey" in col ? col.accessorKey : undefined;
      const headerText =
        typeof col.header === "string"
          ? col.header
          : typeof accessorKey === "string"
            ? accessorKey
            : col.id || "";

      const calculatedMinSize = calculateMinColumnWidth(headerText, {
        enableSorting: tableFeatures.enableSorting,
        enablePinning: tableFeatures.enablePinning,
        enableColumnDragAndDrop: tableFeatures.enableColumnDragAndDrop,
        hasHeaderActions: !!headerActions,
        enableResizing: tableFeatures.enableResizing
      });

      return {
        ...col,
        minSize: calculatedMinSize
      };
    });

    cols.push(...mappedColumns);

    if (actionItems && actionItems.length > 0) {
      cols.push({
        id: "actions",
        header: "Action",
        cell: actionColumnCell
          ? ({ row }) => actionColumnCell({ row })
          : ({ row }) => (
              <ActionCell
                row={row}
                actions={actionItems}
                identifierKey={identifierKey}
              />
            ),
        enableSorting: false,
        enableHiding: false,
        enablePinning: false,
        enableResizing: false,
        size: ACTION_COLUMN_WIDTH,
        minSize: ACTION_COLUMN_WIDTH,
        maxSize: ACTION_COLUMN_WIDTH
      });
    }

    return cols;
  }, [
    columns,
    tableFeatures.enableSelection,
    tableFeatures.enableSorting,
    tableFeatures.enablePinning,
    tableFeatures.enableColumnDragAndDrop,
    tableFeatures.enableResizing,
    headerActions,
    actionItems,
    actionColumnCell,
    identifierKey
  ]);

  const tableData = useMemo(() => data?.data || [], [data?.data]);

  const processedColumnOrder = useMemo(() => {
    const storedOrder = tableState.columnOrder;

    if (!storedOrder || storedOrder.length === 0) {
      return [];
    }

    const userColumnOrder = storedOrder.filter(
      (col) => col !== "select" && col !== "actions"
    );

    const finalOrder: string[] = [];

    if (tableFeatures.enableSelection) {
      finalOrder.push("select");
    }

    finalOrder.push(...userColumnOrder);

    if (actionItems && actionItems.length > 0) {
      finalOrder.push("actions");
    }

    return finalOrder;
  }, [tableState.columnOrder, tableFeatures.enableSelection, actionItems]);

  // Merge column pinning to always pin actions to right
  const effectiveColumnPinning = useMemo(() => {
    const basePinning = tableState.columnPinning || { left: [], right: [] };

    if (actionItems && actionItems.length > 0) {
      const rightPins = basePinning.right || [];
      if (!rightPins.includes("actions")) {
        return {
          ...basePinning,
          right: [...rightPins, "actions"]
        };
      }
    }

    return basePinning;
  }, [tableState.columnPinning, actionItems]);

  const handleSortingChange = useCallback(
    (updaterOrValue: SortingState | ((prev: SortingState) => SortingState)) => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(tableState.sorting)
          : updaterOrValue;

      tableState.setSorting(newSorting);

      if (newSorting.length > 0) {
        const { id, desc } = newSorting[0];
        tableState.setSort(id, desc ? "desc" : "asc");
      } else {
        tableState.setSort(null, null);
      }
    },
    [tableState]
  );

  // React Table instance
  const table = useReactTable({
    data: tableData,
    columns: builtColumns,
    state: {
      sorting: tableState.sorting,
      columnVisibility: tableState.columnVisibility,
      columnOrder: processedColumnOrder,
      columnSizing: tableState.columnSizing,
      columnPinning: effectiveColumnPinning,
      rowSelection: tableState.rowSelection,
      expanded: tableState.expanded,
      pagination: {
        pageIndex: tableState.pagination.page - 1,
        pageSize: tableState.pagination.pageSize
      }
    },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: tableState.setColumnVisibility,
    onColumnOrderChange: tableState.setColumnOrder,
    onColumnSizingChange: tableState.setColumnSizing,
    onColumnPinningChange: tableState.setColumnPinning,
    onRowSelectionChange: (updater) => {
      tableState.setRowSelection(updater, table);
    },
    onExpandedChange: tableState.setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: subRowKey
      ? (row) => row[subRowKey] as TData[] | undefined
      : undefined,
    enableRowSelection: tableFeatures.enableSelection,
    enableSorting: tableFeatures.enableSorting,
    enableColumnResizing: tableFeatures.enableResizing,
    columnResizeMode: "onChange",
    manualPagination: true,
    manualSorting: true,
    defaultColumn: DEFAULT_COLUMN_CONFIG
  });

  // Expose table instance to parent
  useEffect(() => {
    if (onTableReady) {
      onTableReady(table);
    }
  }, [table, onTableReady]);

  const handleRowOrderChange = useCallback(
    (oldIndex: number, newIndex: number) => {
      if (onRowOrderChange) {
        // Get selected row IDs if any rows are selected
        const selectedRowIds = Object.keys(tableState.rowSelection)
          .filter((key) => tableState.rowSelection[key])
          .map((index) => {
            const row = tableData[parseInt(index)];
            return identifierKey ? row[identifierKey as keyof TData] : row;
          })
          .filter((id) => id !== undefined) as (string | number)[];

        onRowOrderChange(
          oldIndex,
          newIndex,
          tableState.pagination,
          tableData,
          selectedRowIds.length > 0 ? selectedRowIds : undefined
        );
      }
    },
    [
      onRowOrderChange,
      tableState.pagination,
      tableState.rowSelection,
      tableData,
      identifierKey
    ]
  );

  const handleColumnOrderChange = useCallback(
    (newOrder: string[]) => {
      tableState.setColumnOrder(newOrder);
    },
    [tableState]
  );

  const totalPages = Math.ceil(
    (data?.total || 0) / tableState.pagination.pageSize
  );
  const selectedCount = Object.keys(tableState.rowSelection).filter(
    (key) => tableState.rowSelection[key]
  ).length;
  const columnsCount = table.getVisibleFlatColumns().length;

  const loading = externalLoading || isLoading || isRefetching;
  const isEmpty = !loading && tableData.length === 0;

  return (
    <TooltipProvider>
      <div className={cn("aero:w-full", className)}>
        {loading ? (
          <div className="aero:flex aero:items-center aero:justify-center aero:h-64">
            <div className="aero:animate-spin aero:rounded-full aero:h-8 aero:w-8 aero:border-b-2 aero:border-brand-primary-600" />
          </div>
        ) : (
          <div className="aero:overflow-auto">
            <Table className="aero:table-fixed">
              <DataTableHeader
                table={table}
                enableRowDragAndDrop={tableFeatures.enableRowDragAndDrop}
                enableColumnDragAndDrop={tableFeatures.enableColumnDragAndDrop}
                enablePinning={tableFeatures.enablePinning}
                enableResizing={tableFeatures.enableResizing}
                enableSorting={tableFeatures.enableSorting}
                enableSelection={tableFeatures.enableSelection}
                enableExpanding={tableFeatures.enableExpanding}
                subRowKey={subRowKey}
                headerActions={headerActions}
                onColumnOrderChange={handleColumnOrderChange}
              />
              <DataTableBody
                table={table}
                enableRowDragAndDrop={tableFeatures.enableRowDragAndDrop}
                enableSelection={tableFeatures.enableSelection}
                enableExpanding={tableFeatures.enableExpanding}
                enableEditing={tableFeatures.enableEditing}
                enableAddSubRow={!!onAddSubRow}
                subRowKey={subRowKey}
                identifierKey={identifierKey}
                editingCell={editingCell}
                onEditingCellChange={setEditingCell}
                onEditValue={onEditValue}
                onRowOrderChange={handleRowOrderChange}
                onRowDoubleClick={onRowDoubleClick}
                onAddSubRow={onAddSubRow}
                onForeignKeySearch={onForeignKeySearch}
                emptyState={emptyState}
                columnsCount={columnsCount}
              />
            </Table>
          </div>
        )}

        {tableFeatures.enablePagination && !loading && !isEmpty && (
          <DataTablePagination
            currentPage={tableState.pagination.page}
            totalPages={totalPages}
            pageSize={tableState.pagination.pageSize}
            totalItems={data?.total || 0}
            visibleItems={tableData.length}
            pageSizes={pageSizes}
            onPageChange={tableState.setPage}
            onPageSizeChange={tableState.setPageSize}
            selectedCount={selectedCount}
            showSelectedCount={tableFeatures.enableSelection}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
